/**
 * EntityRelationships Component
 *
 * Handles displaying and managing relationships for entity types.
 *
 * ARCHITECTURE:
 * - Relationship section rendering is driven by `SECTION_CATALOG`
 * - Section item resolution is centralized in `resolveSectionItems`
 * - Helper utilities are used for labeling, icons, and merge/filter operations
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useRelationshipRules } from "../hooks/useRelationshipRules";
import EntityRelationshipSection from "./EntityRelationshipSection";
import { apiFetch, handle401Error, api } from "../services/api";
import { getEntityTypes } from "../utils/entityTypes";
import { entityAPI } from "../services/entityCache";
import { getEntityTypeIcon } from "../utils/iconUtils";
import {
  buildRelatedEntities,
  getRelationshipIcon,
  getHumanLabel,
  findRelationshipsByType,
  extractEntitiesFromRelationships,
  mergeRelationshipsWithForm,
} from "./relationships/RelationshipHelpers";
import {
  getValidationRule,
} from "./relationships/RelationshipRouting";
import {
  normalizeEntityType,
} from "./relationships/SectionConfig";
import { SECTION_CATALOG } from "./relationships/SectionCatalog";
import { resolveSectionItems } from "./relationships/RelationshipResolver";
import { getRelationshipMapping } from "./relationships/RelationshipTypeMapping";
import { validateMappingsForEntity } from "../utils/relationshipMappingValidator";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole(false);
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

const EntityRelationships = ({
  editingEntity,
  entityForm,
  setEntityForm,
  entities: entitiesProp = [],
  allEntities = {},
  relationships,
  removedItems,
  setRemovedItems,
  readOnly,
  populatedSections = {},
  onEntityTypeChange,
  /** When set, only this catalog section is rendered (inline edit on Read view). */
  onlySectionKey = null,
  /** Hide bulk headers/debug panels for single-section inline editing. */
  compactMode = false,
  /** Parent is still loading relationship rows for this entity. */
  relationshipsLoading = false,
}) => {
  const [sectionErrors, setSectionErrors] = useState({});
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [entityTypesMap, setEntityTypesMap] = useState({}); // Map entity type name -> icon
  const [typeMetaMap, setTypeMetaMap] = useState({}); // Map type value -> { label, color }
  const [fetchedEntities, setFetchedEntities] = useState({}); // Cache for fetched entities
  const [localEntityCache, setLocalEntityCache] = useState({}); // Local cache for fetched entities by ID
  const [referenceStatus, setReferenceStatus] = useState("idle"); // idle | loading | ready | error
  const [referenceError, setReferenceError] = useState(null);
  const [loadingEntityTypes, setLoadingEntityTypes] = useState(new Set()); // Track which entity types are currently loading
  const [neighborsHydrationLoading, setNeighborsHydrationLoading] =
    useState(false);
  /** Local overlay for relationship attrs after Edit details (keyed by rel: / pending:). */
  const [relationshipAttributeOverrides, setRelationshipAttributeOverrides] =
    useState({});
  const pendingFetches = useRef(new Set()); // Track ongoing fetches
  const loadingTypesRef = useRef(new Set()); // Track entity type loads
  const fetchedTypesRef = useRef(new Set()); // Track completed type loads
  const neighborsFetchGenRef = useRef(0);
  // Prefer editingEntity for type: entityForm can lag one render behind after switching entities.
  const effectiveEntityTypeRaw =
    editingEntity?.entity_type ||
    editingEntity?.type ||
    entityForm?.type ||
    "";
  const effectiveEntityType = normalizeEntityType(effectiveEntityTypeRaw);

  const resolverCurrentEntity = useMemo(() => {
    if (!editingEntity) return null;
    const canonical =
      effectiveEntityType ||
      normalizeEntityType(
        editingEntity.entity_type || editingEntity.type || entityForm?.type || ""
      );
    return {
      ...editingEntity,
      type: canonical,
      entity_type: editingEntity.entity_type || canonical,
    };
  }, [editingEntity, effectiveEntityType, entityForm?.type]);

  const normalizeType = useMemo(() => {
    const aliasMap = {
      application: [
        "application",
        "applicationcomponent",
        "application component",
      ],
      "technology-component": [
        "technology-component",
        "technologycomponent",
        "itcomponent",
        "it component",
      ],
      "business-capability": [
        "business-capability",
        "businesscapability",
        "business capability",
      ],
      "business-context": [
        "business-context",
        "businesscontext",
        "business context",
      ],
      "data-object": ["data-object", "dataobject", "data object"],
      interface: ["interface", "applicationinterface", "application interface"],
      organization: [
        "organization",
        "organisation",
        "org",
        "org-unit",
        "org unit",
      ],
      initiative: ["initiative"],
      platform: ["platform"],
      provider: ["provider"],
      objective: ["objective", "goal"],
      "solution-component": ["solution-component", "solution component"],
      "business-actor": ["business-actor", "businessactor"],
      "business-process": ["business-process", "businessprocess"],
    };

    const formatKey = (val) =>
      String(val || "")
        .toLowerCase()
        .trim()
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .replace(/\s/g, "-");

    const aliasToCanonical = {};
    Object.entries(aliasMap).forEach(([canonical, aliases]) => {
      aliases.forEach((alias) => {
        aliasToCanonical[formatKey(alias)] = canonical;
      });
    });

    return (value) => {
      if (!value) return "";
      const key = formatKey(value);
      return aliasToCanonical[key] || normalizeEntityType(value);
    };
  }, []);

  const requiredRelatedTypes = useMemo(() => {
    const defs = SECTION_CATALOG[effectiveEntityType] || [];
    const typeSet = new Set();
    defs.forEach((section) => {
      (section.relatedEntityTypes || []).forEach((t) => {
        const canonical = normalizeType(t);
        if (canonical) {
          typeSet.add(canonical);
        }
      });
    });
    const typesArray = Array.from(typeSet);
    debugLog(
      `🔍 EntityRelationships: requiredRelatedTypes for "${effectiveEntityTypeRaw}":`,
      typesArray
    );
    return Array.from(typeSet).sort();
  }, [effectiveEntityType, effectiveEntityTypeRaw, normalizeType]);

  const combinedAllEntities = useMemo(() => {
    if (!allEntities && !localEntityCache) return {};
    return {
      ...(allEntities || {}),
      ...(localEntityCache || {}),
    };
  }, [allEntities, localEntityCache]);

  // Merge lightweight entity list with the full allEntities cache so the Add dialogs always have options
  const entities = useMemo(() => {
    const merged = new Map();
    (entitiesProp || []).forEach((entity) => {
      if (entity?.id) {
        merged.set(entity.id, {
          ...entity,
          type: normalizeType(entity.type || entity.entity_type),
          entity_type: normalizeType(entity.entity_type || entity.type),
        });
      }
    });

    Object.entries(combinedAllEntities || {}).forEach(
      ([entityId, entityData]) => {
        if (!entityData) return;
        const normalizedEntity = {
          id: entityData.id || entityId,
          name: entityData.name,
          type: normalizeType(entityData.type || entityData.entity_type),
          entity_type: normalizeType(entityData.entity_type || entityData.type),
          description: entityData.description,
          subtype: entityData.subtype,
          status: entityData.status,
        };
        if (!merged.has(normalizedEntity.id)) {
          merged.set(normalizedEntity.id, normalizedEntity);
        }
      }
    );

    // Safely process fetchedEntities - handle various data structures
    if (fetchedEntities && typeof fetchedEntities === "object") {
      try {
        const values = Object.values(fetchedEntities);
        if (Array.isArray(values)) {
          values.forEach((value) => {
            // Skip null, undefined, or non-array/non-object values
            if (!value || typeof value !== "object") {
              return;
            }
            // Handle both array of entities and single entity object
            const entitiesList = Array.isArray(value)
              ? value
              : value?.id
                ? [value]
                : [];
            // Ensure entitiesList is actually an array before forEach
            if (Array.isArray(entitiesList)) {
              entitiesList.forEach((entity) => {
                if (
                  entity &&
                  typeof entity === "object" &&
                  entity.id &&
                  !merged.has(entity.id)
                ) {
                  merged.set(entity.id, {
                    ...entity,
                    type: normalizeType(entity.type || entity.entity_type),
                    entity_type: normalizeType(
                      entity.entity_type || entity.type
                    ),
                  });
                }
              });
            }
          });
        }
      } catch (error) {
        debugWarn(
          "Error processing fetchedEntities:",
          error,
          fetchedEntities
        );
      }
    }

    return Array.from(merged.values());
  }, [entitiesProp, combinedAllEntities, fetchedEntities, normalizeType]);

  /** Built once per entities/cache change — avoids O(sections × allEntities) work inside each section. */
  const entitiesForResolver = useMemo(() => {
    const allList = Object.keys(combinedAllEntities || {}).map((id) => ({
      id,
      name: combinedAllEntities[id]?.name,
      entity_type:
        combinedAllEntities[id]?.entity_type ||
        combinedAllEntities[id]?.type ||
        "Unknown",
      type:
        combinedAllEntities[id]?.type ||
        combinedAllEntities[id]?.entity_type ||
        "Unknown",
      description: combinedAllEntities[id]?.description,
    }));
    return [...entities, ...allList];
  }, [entities, combinedAllEntities]);

  // Ensure all endpoint entities for the current record's relationships are loaded.
  // This makes section routing robust even when paginated/cached entity lists are partial.
  useEffect(() => {
    if (!editingEntity?.id || !Array.isArray(relationships) || relationships.length === 0) {
      setNeighborsHydrationLoading(false);
      return;
    }

    const neighborIds = new Set();
    relationships.forEach((rel) => {
      if (!rel) return;
      const isSource = rel.source_entity_id === editingEntity.id;
      const isTarget = rel.target_entity_id === editingEntity.id;
      if (!isSource && !isTarget) return;
      const otherId = isSource ? rel.target_entity_id : rel.source_entity_id;
      if (otherId) neighborIds.add(String(otherId));
    });

    if (neighborIds.size === 0) return;

    const known = new Set();
    (entities || []).forEach((e) => e?.id && known.add(String(e.id)));
    Object.keys(combinedAllEntities || {}).forEach((id) => id && known.add(String(id)));
    Object.values(fetchedEntities || {}).forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach((e) => e?.id && known.add(String(e.id)));
    });

    const missing = Array.from(neighborIds).filter((id) => !known.has(id));
    if (missing.length === 0) {
      setNeighborsHydrationLoading(false);
      return;
    }

    let cancelled = false;
    const fetchGen = ++neighborsFetchGenRef.current;
    setNeighborsHydrationLoading(true);

    const fetchMissingNeighbors = async () => {
      try {
        const chunkSize = 40;
        const additions = {};
        const byType = {};

        for (let i = 0; i < missing.length; i += chunkSize) {
          const chunk = missing.slice(i, i + chunkSize);
          const response = await apiFetch(
            `/api/entities/batch?ids=${encodeURIComponent(chunk.join(","))}`
          );
          if (!response.ok) continue;

          const data = await response.json();
          const rows = Array.isArray(data?.entities) ? data.entities : [];
          rows.forEach((row) => {
            if (!row?.id) return;
            const canonicalType = normalizeType(row.type || row.entity_type);
            const normalized = {
              id: row.id,
              name: row.name || `Entity ${String(row.id).slice(0, 8)}...`,
              type: canonicalType,
              entity_type: canonicalType,
              description: row.description,
              subtype: row.subtype,
              status: row.status,
            };
            additions[row.id] = normalized;
            if (!byType[canonicalType]) byType[canonicalType] = [];
            byType[canonicalType].push(normalized);
          });
        }

        if (cancelled || Object.keys(additions).length === 0) return;

        setLocalEntityCache((prev) => ({ ...prev, ...additions }));
        setFetchedEntities((prev) => {
          const next = { ...prev };
          Object.entries(byType).forEach(([t, list]) => {
            const prevList = Array.isArray(next[t]) ? next[t] : [];
            const seen = new Set(prevList.map((e) => e.id));
            const merged = [...prevList];
            list.forEach((e) => {
              if (!seen.has(e.id)) {
                seen.add(e.id);
                merged.push(e);
              }
            });
            next[t] = merged;
          });
          return next;
        });
      } catch (e) {
        debugWarn("Failed to fetch missing relationship endpoint entities:", e);
      } finally {
        if (!cancelled && fetchGen === neighborsFetchGenRef.current) {
          setNeighborsHydrationLoading(false);
        }
      }
    };

    fetchMissingNeighbors();
    return () => {
      cancelled = true;
    };
  }, [
    editingEntity?.id,
    relationships,
    entities,
    combinedAllEntities,
    fetchedEntities,
    normalizeType,
  ]);

  const fetchEntitiesByType = useCallback(
    async (relatedType) => {
      const canonicalType = normalizeType(relatedType);
      debugLog(
        `🔍 fetchEntitiesByType called: relatedType="${relatedType}", canonicalType="${canonicalType}"`
      );

      if (
        !canonicalType ||
        loadingTypesRef.current.has(canonicalType) ||
        fetchedTypesRef.current.has(canonicalType)
      ) {
        debugLog(
          `🔍 fetchEntitiesByType: Skipping fetch for "${canonicalType}" - already loaded or in progress`
        );
        return;
      }

      loadingTypesRef.current.add(canonicalType);
      setLoadingEntityTypes((prev) => new Set(prev).add(canonicalType));
      try {
        const aggregated = [];
        const additions = {};
        const pageSize = 500;
        let page = 1;
        let totalPages = 1;

        while (page <= totalPages) {
          const url = `/api/entities-paginated?page=${page}&page_size=${pageSize}&entity_type=${encodeURIComponent(
            canonicalType
          )}&include_relationships=false`;
          debugLog(`🔍 fetchEntitiesByType: Fetching from URL: ${url}`);
          const response = await apiFetch(url, { timeout: 20000 });
          if (!response.ok) {
            const errorText = await response
              .text()
              .catch(() => "Unknown error");
            debugError(
              `❌ fetchEntitiesByType: API error for type "${canonicalType}" on page ${page}:`,
              response.status,
              errorText
            );
            break;
          }

          const data = await response.json();
          const entitiesFromApi = data?.data?.entities || data?.entities || [];
          const pagination = data?.data?.pagination || data?.pagination;
          totalPages = pagination?.total_pages || totalPages;

          debugLog(
            `🔍 fetchEntitiesByType: Received ${entitiesFromApi.length} entities for type "${canonicalType}" (page ${page}/${totalPages})`
          );

          entitiesFromApi.forEach((entity) => {
            const normalizedType = normalizeType(
              entity.type || entity.entity_type
            );
            const normalizedEntityType = normalizeType(
              entity.entity_type || entity.type
            );
            const normalizedEntity = {
              ...entity,
              id: entity.id,
              name: entity.name,
              type: normalizedType,
              entity_type: normalizedEntityType,
              description: entity.description,
              subtype: entity.subtype,
              status: entity.status,
            };
            aggregated.push(normalizedEntity);
            additions[entity.id] = {
              id: normalizedEntity.id,
              name: normalizedEntity.name,
              type: normalizedEntity.type,
              entity_type: normalizedEntity.entity_type,
              description: normalizedEntity.description,
              subtype: normalizedEntity.subtype,
              status: normalizedEntity.status,
            };
          });

          if (!pagination || page >= totalPages) {
            break;
          }

          page += 1;
        }

        setFetchedEntities((prev) => ({
          ...prev,
          [canonicalType]: aggregated,
        }));
        if (Object.keys(additions).length > 0) {
          setLocalEntityCache((prev) => ({
            ...prev,
            ...additions,
          }));
        }
        fetchedTypesRef.current.add(canonicalType);
        setLoadingEntityTypes((prev) => {
          const next = new Set(prev);
          next.delete(canonicalType);
          return next;
        });
        debugLog(
          `✅ fetchEntitiesByType: Stored ${aggregated.length} entities for type "${canonicalType}" across ${totalPages} page(s)`
        );
      } catch (error) {
        // Check if this is a 401 error and handle logout
        const is401 =
          error?.is401Error ||
          error?.status === 401 ||
          error?.message?.includes("Session expired") ||
          error?.message?.includes("401");

        if (is401) {
          handle401Error(error);
          // Don't continue processing on 401
          return;
        }

        debugError(
          `❌ fetchEntitiesByType: Failed to fetch entities for type ${canonicalType}:`,
          error
        );
        setFetchedEntities((prev) => ({
          ...prev,
          [canonicalType]: [],
        }));
        setLoadingEntityTypes((prev) => {
          const next = new Set(prev);
          next.delete(canonicalType);
          return next;
        });
      } finally {
        loadingTypesRef.current.delete(canonicalType);
      }
    },
    [normalizeType]
  );

  const sectionDefinitions = useMemo(() => {
    return SECTION_CATALOG[effectiveEntityType]
      ? SECTION_CATALOG[effectiveEntityType]
      : Object.entries(populatedSections).map(([sectionKey, sectionData]) => ({
          key: sectionKey,
          label: sectionData.title,
          relatedEntityTypes: [sectionData.relatedType],
          direction: sectionData.direction,
        }));
  }, [effectiveEntityType, populatedSections]);

  const relationshipSectionKeys = useMemo(
    () => sectionDefinitions.map((defOrEntry) => defOrEntry.key || defOrEntry),
    [sectionDefinitions]
  );

  /** Invalidates relationship resolution only when relationship section arrays change, not other form fields. */
  const relFormFingerprint = useMemo(
    () =>
      relationshipSectionKeys
        .map((k) => {
          const arr = entityForm?.[k];
          const ids = Array.isArray(arr)
            ? arr
                .map((x) => x?.id)
                .filter(Boolean)
                .slice()
                .sort()
                .join(",")
            : "";
          return `${k}:${ids}`;
        })
        .join("|"),
    [entityForm, relationshipSectionKeys]
  );

  const removedItemsFingerprint = useMemo(
    () =>
      relationshipSectionKeys
        .map((k) => {
          const arr = removedItems?.[k];
          const ids = Array.isArray(arr)
            ? arr.map(String).slice().sort().join(",")
            : "";
          return `${k}:${ids}`;
        })
        .join("|"),
    [removedItems, relationshipSectionKeys]
  );

  const relationshipsSig = useMemo(() => {
    const r = relationships || [];
    if (r.length === 0) return "0";
    return `${r.length}:${r
      .map((rel) =>
        [
          rel?.id,
          rel?.type,
          rel?.source_entity_id,
          rel?.target_entity_id,
        ].join(":")
      )
      .join(";")}`;
  }, [relationships]);

  const sectionConfigsMemo = useMemo(() => {
    const missingTypes = new Set();
    const configs = sectionDefinitions.map((defOrEntry) => {
      const sectionKey = defOrEntry.key || defOrEntry;
      const sectionData = SECTION_CATALOG[effectiveEntityType]
        ? defOrEntry
        : populatedSections[defOrEntry.key] || {
            title: defOrEntry.label,
            relatedType: (defOrEntry.relatedEntityTypes || [])[0],
            direction: defOrEntry.direction,
          };

      const rawRelatedTypes =
        Array.isArray(defOrEntry.relatedEntityTypes) &&
        defOrEntry.relatedEntityTypes.length > 0
          ? defOrEntry.relatedEntityTypes
          : Array.isArray(sectionData.relatedEntityTypes) &&
              sectionData.relatedEntityTypes.length > 0
            ? sectionData.relatedEntityTypes
            : sectionData.relatedType
              ? [sectionData.relatedType]
              : [];

      const normalizedRelatedTypes = rawRelatedTypes
        .filter(Boolean)
        .map((type) => normalizeType(type));

      // Edit mode uses lazy, on-demand search in the Add dialog.
      // Skip expensive pre-computation of full candidate lists on initial render.
      if (!readOnly) {
        return {
          sectionKey,
          sectionData,
          defOrEntry,
          rawRelatedTypes,
          normalizedRelatedTypes,
          availableItems: [],
        };
      }

      // SIMPLE APPROACH: Get ALL entities from ALL sources, filter by type, then exclude already-added
      let availableItems = [];

      // Collect ALL entities from ALL sources
      const allSourceEntities = [];

      // Source 1: Main entities list
      entities.forEach((e) => {
        if (e && e.id) allSourceEntities.push(e);
      });

      // Source 2: combinedAllEntities cache
      Object.entries(combinedAllEntities || {}).forEach(([entityId, e]) => {
        if (e && typeof e === "object") {
          // Use entityId as fallback if e.id is missing
          const entityIdValue = e.id || entityId;
          if (entityIdValue) {
            allSourceEntities.push({
              id: entityIdValue,
              name: e.name || `Entity ${entityIdValue.substring(0, 8)}...`,
              type: e.type || e.entity_type,
              entity_type: e.entity_type || e.type,
              description: e.description,
              subtype: e.subtype,
              status: e.status,
            });
          }
        }
      });

      // Source 3: fetchedEntities (from reference API)
      normalizedRelatedTypes.forEach((normalizedType) => {
        const fetched = fetchedEntities[normalizedType];
        if (Array.isArray(fetched)) {
          fetched.forEach((e) => {
            if (e && e.id) allSourceEntities.push(e);
          });
        }
      });

      // Remove duplicates by ID
      const uniqueEntities = new Map();
      allSourceEntities.forEach((e) => {
        if (e && e.id && !uniqueEntities.has(e.id)) {
          uniqueEntities.set(e.id, e);
        }
      });

      // Filter by matching normalized types ONLY
      availableItems = Array.from(uniqueEntities.values()).filter((e) => {
        if (!e || !e.id) return false;
        // Normalize the entity's type
        const entityType = normalizeType(e.type || e.entity_type || "");
        // Check if it matches any of the required types
        const matches = normalizedRelatedTypes.includes(entityType);
        return matches;
      });

      // Log for debugging
      if (
        normalizedRelatedTypes.some(
          (t) =>
            t === "technology-component" ||
            t === "business-context" ||
            t === "interface"
        )
      ) {
        debugLog(
          `🔍 EntityRelationships: Section "${sectionKey}" - Required types: ${normalizedRelatedTypes.join(", ")}, Found: ${availableItems.length} entities`
        );
        debugLog(
          `🔍 EntityRelationships: Sources - entities: ${entities.length}, combinedAllEntities: ${Object.keys(combinedAllEntities || {}).length}, fetchedEntities keys: ${Object.keys(fetchedEntities).join(", ")}`
        );
        normalizedRelatedTypes.forEach((t) => {
          const fetched = fetchedEntities[t];
          debugLog(
            `🔍 EntityRelationships: fetchedEntities["${t}"] = ${Array.isArray(fetched) ? fetched.length : "not array"} entities`
          );
        });
        if (sectionKey === "data_object_transferred_by_interface") {
          debugLog(
            `🔍 EntityRelationships: data_object_transferred_by_interface - availableItems sample:`,
            availableItems.slice(0, 5).map((e) => ({
              id: e.id,
              name: e.name,
              type: e.type || e.entity_type,
            }))
          );
        }
      }

      // Step 4: Check if we need to fetch more entities by type (only if still empty)
      if (availableItems.length === 0 && normalizedRelatedTypes.length > 0) {
        normalizedRelatedTypes.forEach((type) => {
          const fetched = fetchedEntities[type];
          const hasFetched = Array.isArray(fetched) && fetched.length > 0;
          if (!hasFetched) {
            missingTypes.add(type);
            debugLog(
              `🔍 EntityRelationships: Missing type "${type}" for section "${sectionKey}" (will trigger fetch)`
            );
          } else {
            debugLog(
              `✅ EntityRelationships: Already have ${fetched.length} entities for type "${type}" in section "${sectionKey}"`
            );
          }
        });
      }

      // Debug logging for platform sections
      if (sectionKey.includes("platform")) {
        debugLog(`🔍 Platform section "${sectionKey}" analysis:`, {
          normalizedRelatedTypes,
          availableItemsCount: availableItems.length,
          entitiesCount: entities.length,
          allEntitiesCount: Object.keys(combinedAllEntities || {}).length,
          fetchedEntitiesKeys: Object.keys(fetchedEntities || {}),
          fetchedEntitiesForPlatform: fetchedEntities["platform"]?.length || 0,
          sampleAvailableItems: availableItems.slice(0, 3).map((e) => ({
            id: e.id,
            name: e.name,
            type: e.type,
            entity_type: e.entity_type,
          })),
        });
      }

      if (availableItems.length === 0 && normalizedRelatedTypes.length > 0) {
        debugLog(
          `⚠️ EntityRelationships: No available items for section "${sectionKey}" with types:`,
          normalizedRelatedTypes
        );
      } else if (availableItems.length > 0) {
        debugLog(
          `✅ EntityRelationships: Found ${availableItems.length} available items for section "${sectionKey}"`
        );
      }

      // Ensure availableItems is always an array
      const finalAvailableItems = Array.isArray(availableItems)
        ? availableItems
        : [];

      return {
        sectionKey,
        sectionData,
        defOrEntry,
        rawRelatedTypes,
        normalizedRelatedTypes,
        availableItems: finalAvailableItems,
      };
    });
    return {
      configs,
      missingTypes: Array.from(missingTypes),
    };
  }, [
    sectionDefinitions,
    effectiveEntityType,
    populatedSections,
    readOnly,
    entities,
    combinedAllEntities,
    fetchedEntities,
    normalizeEntityType,
    normalizeType,
  ]);
  const sectionHealthWarnedRef = useRef(new Set());

  const getAllowedRelationshipTypes = useCallback((defOrEntry, sectionData) => {
    const fromDef = Array.isArray(defOrEntry?.relationshipTypes)
      ? defOrEntry.relationshipTypes
      : [];
    const fromSectionData =
      sectionData?.relationshipType != null
        ? [sectionData.relationshipType]
        : [];
    return Array.from(
      new Set(
        [...fromDef, ...fromSectionData]
          .map((t) => String(t || "").trim().toLowerCase())
          .filter(Boolean)
      )
    );
  }, []);

  const getExpectedRelationshipMatches = useCallback(
    (sectionKey, defOrEntry, sectionData) => {
      if (!editingEntity?.id || !Array.isArray(relationships)) return [];
      const allowedTypes = getAllowedRelationshipTypes(defOrEntry, sectionData);
      const allowedRelatedTypes = new Set(
        (Array.isArray(defOrEntry?.relatedEntityTypes)
          ? defOrEntry.relatedEntityTypes
          : []
        )
          .map((t) => normalizeType(t))
          .filter(Boolean)
      );
      const direction = String(sectionData?.direction || defOrEntry?.direction || "either")
        .toLowerCase()
        .trim();
      const hasTypeFilter = allowedTypes.length > 0;
      const resolveOtherType = (rel, isSourceDirection) => {
        const otherId = isSourceDirection ? rel.target_entity_id : rel.source_entity_id;
        const known =
          (entities || []).find((e) => e?.id === otherId) ||
          combinedAllEntities?.[otherId] ||
          null;
        const hinted = isSourceDirection ? rel.target_entity_type : rel.source_entity_type;
        return normalizeType(known?.type || known?.entity_type || hinted || "");
      };
      return relationships.filter((rel) => {
        if (!rel) return false;
        const typeLower = String(rel.type || "").toLowerCase().trim();
        if (hasTypeFilter && !allowedTypes.includes(typeLower)) return false;
        let isMatchDirection = false;
        let otherType = "";
        if (direction === "incoming") {
          isMatchDirection = rel.target_entity_id === editingEntity.id;
          if (isMatchDirection) {
            otherType = resolveOtherType(rel, false);
          }
        } else if (direction === "outgoing") {
          isMatchDirection = rel.source_entity_id === editingEntity.id;
          if (isMatchDirection) {
            otherType = resolveOtherType(rel, true);
          }
        } else {
          const touchesEntity =
            rel.source_entity_id === editingEntity.id ||
            rel.target_entity_id === editingEntity.id;
          isMatchDirection = touchesEntity;
          if (touchesEntity) {
            const isSourceDirection = rel.source_entity_id === editingEntity.id;
            otherType = resolveOtherType(rel, isSourceDirection);
          }
        }
        if (!isMatchDirection) return false;
        // Strict metamodel filter: for typed sections, endpoint type must match.
        if (allowedRelatedTypes.size > 0) {
          if (!otherType) return false;
          return allowedRelatedTypes.has(otherType);
        }
        return (
          rel.source_entity_id === editingEntity.id ||
          rel.target_entity_id === editingEntity.id
        );
      });
    },
    [
      editingEntity?.id,
      relationships,
      getAllowedRelationshipTypes,
      entities,
      combinedAllEntities,
      normalizeType,
    ]
  );

  const buildFallbackItemsFromRelationships = useCallback(
    (rels) => {
      if (!editingEntity?.id || !Array.isArray(rels) || rels.length === 0) return [];
      const uniq = new Map();
      const allList = Array.isArray(entities) ? entities : [];
      rels.forEach((rel) => {
        const isSource = rel.source_entity_id === editingEntity.id;
        const otherId = isSource ? rel.target_entity_id : rel.source_entity_id;
        if (!otherId) return;
        const dedupeKey =
          rel.id != null && rel.id !== "" ? `rel:${rel.id}` : `ent:${otherId}`;
        if (uniq.has(dedupeKey)) return;
        const known =
          allList.find((e) => e?.id === otherId) ||
          combinedAllEntities?.[otherId] ||
          null;
        uniq.set(dedupeKey, {
          id: otherId,
          relationshipId: rel.id,
          name: known?.name || `Entity ${String(otherId).slice(0, 8)}...`,
          type:
            normalizeType(
              known?.type ||
                known?.entity_type ||
                (isSource ? rel.target_entity_type : rel.source_entity_type)
            ) || "unknown",
          entity_type:
            normalizeType(
              known?.entity_type ||
                known?.type ||
                (isSource ? rel.target_entity_type : rel.source_entity_type)
            ) || "unknown",
          description: known?.description || rel.description || "",
          activeFrom: rel.active_from || null,
          activeUntil: rel.active_until || null,
          status: rel.status || null,
        });
      });
      return Array.from(uniq.values());
    },
    [editingEntity?.id, entities, combinedAllEntities, normalizeType]
  );

  // Set reference status to idle since we're using lazy loading
  useEffect(() => {
    setReferenceStatus("idle");
  }, []);

  // NOTE: removed duplicate per-id prefetch loop; batch hydration effect above already
  // resolves missing neighbors and is significantly cheaper for large relationship sets.

  // Use dynamic relationship rules from backend
  const {
    getRulesForEntityType,
    loading: rulesLoading,
    error: rulesError,
  } = useRelationshipRules();

  // Fetch relationship types from backend on mount
  useEffect(() => {
    const fetchRelationshipTypes = async () => {
      try {
        const response = await apiFetch("/api/relationship-types");
        if (response.ok) {
          const data = await response.json();
          // Extract active relationship type names
          const activeTypes = (data.relationship_types || [])
            .filter((rt) => rt.is_active)
            .map((rt) => rt.name.toLowerCase()); // Convert to lowercase to match database
          setRelationshipTypes(activeTypes);
        }
      } catch (error) {
        debugError("Error fetching relationship types:", error);
      }
    };
    fetchRelationshipTypes();
  }, []);

  // Fetch entity types to get icons
  useEffect(() => {
    const fetchEntityTypes = async () => {
      try {
        const types = await getEntityTypes();
        // Create a map of entity type name -> icon
        const map = {};
        const meta = {};
        types.forEach((type) => {
          // Store both the name (from API) and the icon
          map[type.value] = type.icon ? getEntityTypeIcon(type.icon) : "📦";
          // Also map display names for flexibility
          if (type.label) {
            map[type.label] = type.icon ? getEntityTypeIcon(type.icon) : "📦";
          }
          // Build type metadata map for label/color
          const key = String(type.value || "").toLowerCase();
          meta[key] = {
            label: type.label || type.value,
            color: type.color || "#4B5563",
          };
        });
        setEntityTypesMap(map);
        setTypeMetaMap(meta);
      } catch (error) {
        debugError("Error fetching entity types:", error);
      }
    };
    fetchEntityTypes();
  }, []);

  // Dynamic rules-driven helpers
  const rulesForType = React.useMemo(() => {
    const rules = getRulesForEntityType(effectiveEntityTypeRaw) || [];
    debugLog(
      `🔍 [EntityRelationships] rulesForType for "${effectiveEntityTypeRaw}":`,
      {
        rulesCount: rules.length,
        entityFormType: effectiveEntityTypeRaw,
        rules: rules.map((r) => ({
          key: r.key,
          relationshipType: r.relationshipType,
          direction: r.direction,
          relatedEntityType: r.relatedEntityType,
        })),
        allKeys: rules.map((r) => r.key),
      }
    );
    return rules;
  }, [effectiveEntityTypeRaw, getRulesForEntityType]);

  /**
   * Match EntityReadOnlyView: for catalog-backed types, resolveSectionItems uses
   * SECTION_CATALOG + hardcoded RelationshipTypeMapping only (relationshipRules: []).
   * Passing the full server rule list forces expensive per-section scans and diverges from Read.
   */
  const relationshipRulesForResolver = React.useMemo(() => {
    if (effectiveEntityType && SECTION_CATALOG[effectiveEntityType]) return [];
    return rulesForType;
  }, [effectiveEntityType, rulesForType]);

  /**
   * Single batched pass over sections — Edit matched Read cost before when each row
   * rebuilt entitiesForResolver and called resolveSectionItems on every parent re-render.
   */
  const resolvedSectionItemsMap = useMemo(() => {
    const map = {};
    const configs = sectionConfigsMemo.configs || [];
    for (let i = 0; i < configs.length; i++) {
      const cfg = configs[i];
      const sectionKey = cfg.sectionKey;
      const sectionData = cfg.sectionData;
      const defOrEntry = cfg.defOrEntry;
      const rawRelatedTypes = cfg.rawRelatedTypes || [];
      const resolverSectionDef = defOrEntry?.key
        ? defOrEntry
        : {
            key: sectionKey,
            label: sectionData.title || sectionData.label || sectionKey,
            direction: sectionData.direction || "either",
            relatedEntityTypes: rawRelatedTypes,
            relationshipTypes: sectionData.relationshipType
              ? [sectionData.relationshipType]
              : [],
          };
      let items = resolveSectionItems({
        currentEntity: resolverCurrentEntity,
        relationships,
        entities: entitiesForResolver,
        allEntities: combinedAllEntities,
        fetchedEntities,
        sectionDef: resolverSectionDef,
        entityForm,
        removedItems,
        relationshipRules: relationshipRulesForResolver,
      });

      const expectedRels = getExpectedRelationshipMatches(
        sectionKey,
        defOrEntry,
        sectionData
      );
      const hasResolverGap =
        Array.isArray(items) &&
        items.length === 0 &&
        expectedRels.length > 0;
      if (hasResolverGap) {
        const fallbackItems = buildFallbackItemsFromRelationships(expectedRels);
        if (fallbackItems.length > 0) {
          items = fallbackItems;
          const warnKey = `${editingEntity?.id || "new"}:${sectionKey}`;
          if (!sectionHealthWarnedRef.current.has(warnKey)) {
            sectionHealthWarnedRef.current.add(warnKey);
            debugWarn(
              `⚠️ [EntityRelationships] Resolver gap for section "${sectionKey}": expected ${expectedRels.length} relationship(s), rendered via fallback ${fallbackItems.length}.`
            );
          }
        }
      }
      map[sectionKey] = items;
    }
    return map;
  }, [
    sectionConfigsMemo.configs,
    resolverCurrentEntity,
    relationshipsSig,
    entitiesForResolver,
    combinedAllEntities,
    fetchedEntities,
    relationshipRulesForResolver,
    relFormFingerprint,
    removedItemsFingerprint,
    getExpectedRelationshipMatches,
    buildFallbackItemsFromRelationships,
  ]);

  const relationshipsPopulateLoading = useMemo(() => {
    if (relationshipsLoading || neighborsHydrationLoading) return true;
    if (!editingEntity?.id || rulesLoading) return false;

    const entityRels = (relationships || []).filter(
      (rel) =>
        rel &&
        (rel.source_entity_id === editingEntity.id ||
          rel.target_entity_id === editingEntity.id)
    );

    if (entityRels.length === 0) return false;

    for (const cfg of sectionConfigsMemo.configs || []) {
      const expected = getExpectedRelationshipMatches(
        cfg.sectionKey,
        cfg.defOrEntry,
        cfg.sectionData
      );
      const items = resolvedSectionItemsMap[cfg.sectionKey] || [];
      if (expected.length > 0 && items.length === 0) {
        return true;
      }
    }

    return false;
  }, [
    relationshipsLoading,
    neighborsHydrationLoading,
    editingEntity?.id,
    rulesLoading,
    relationships,
    sectionConfigsMemo.configs,
    getExpectedRelationshipMatches,
    resolvedSectionItemsMap,
  ]);

  const configsToRender = useMemo(() => {
    const list = sectionConfigsMemo.configs || [];
    if (!onlySectionKey) return list;
    return list.filter((c) => c.sectionKey === onlySectionKey);
  }, [sectionConfigsMemo.configs, onlySectionKey]);

  const getSectionKeyForRule = (rule) => {
    if (rule.key === "interface_provided_by_app") {
      return "interface_provided_by_app";
    }
    if (rule.key === "interface_consumed_by_app") {
      return "interface_consumed_by_app";
    }
    const type = rule.relatedEntityType;
    if (type === "interface") {
      // Use rule key for specific interface sections
      if (rule.key === "interface_provided_by_app") {
        return "interface_provided_by_app";
      } else if (rule.key === "interface_consumed_by_app") {
        return "interface_consumed_by_app";
      } else {
        // Fallback to generic mapping
        return rule.direction === "incoming"
          ? "consumed_interfaces"
          : "provided_interfaces";
      }
    }
    if (type === "business-capability") return "related_business_capabilities";
    if (type === "business-context") return "related_business_contexts";
    if (type === "data-object") return "related_data_objects";
    if (type === "technology-component") return "related_it_components";
    if (type === "initiative") return "related_initiatives";
    if (type === "application") return "related_applications";
    if (type === "business-actor") return "related_business_actors";
    if (type === "organization") return "related_organizations";
    if (type === "provider") return "related_providers";
    if (type === "objective") return "related_goals";

    // Special handling for successor relationships
    if (rule.key && rule.key.includes("_successor")) {
      return "related_successors";
    }

    // Special handling for parent relationships
    if (rule.key && rule.key.includes("_parent")) {
      return `related_${rule.relatedEntityType.toLowerCase()}_parents`;
    }

    return null;
  };

  // Helper to build merged related list (DB + form), filtered by entity type and section key
  const buildRelated = (relatedEntityType, sectionKey) => {
    // Get relationship types from server rules or hardcoded mapping for this specific section
    let allowedRelationshipTypes = [];

    // First, try to get from server rules
    const entityTypeForMapping =
      editingEntity?.type || editingEntity?.entity_type || entityForm.type;
    if (rulesForType && rulesForType.length > 0) {
      const matchingRules = rulesForType.filter(
        (rule) => rule.key === sectionKey
      );
      if (matchingRules.length > 0) {
        allowedRelationshipTypes = matchingRules
          .map((rule) => rule.relationshipType)
          .filter(Boolean);
        debugLog(`🔍 [buildRelated] Using server rules for ${sectionKey}:`, {
          allowedRelationshipTypes,
          allowedRelationshipTypesLower: allowedRelationshipTypes.map((t) =>
            String(t).toLowerCase()
          ),
          rulesCount: matchingRules.length,
          matchingRules: matchingRules.map((r) => ({
            key: r.key,
            relationshipType: r.relationshipType,
          })),
        });
        // API metamodel rules are authoritative for *allowed* new links, but they
        // must not hide edges that already exist (e.g. `owns` left after org→provider).
        // Union with the frontend relationship registry for this section.
        const catalogMapping = getRelationshipMapping(
          entityTypeForMapping,
          sectionKey
        );
        if (catalogMapping?.relationshipTypes?.length) {
          const merged = new Set(
            [
              ...allowedRelationshipTypes,
              ...catalogMapping.relationshipTypes,
            ].map((t) => String(t || "").toLowerCase().trim())
          );
          allowedRelationshipTypes = Array.from(merged);
          debugLog(
            `🔍 [buildRelated] Unioned catalog types with server rules for ${sectionKey}:`,
            { allowedRelationshipTypes }
          );
        }
      }
    }

    // Fallback to hardcoded mapping
    if (allowedRelationshipTypes.length === 0) {
      const mapping = getRelationshipMapping(entityTypeForMapping, sectionKey);
      if (mapping && mapping.relationshipTypes) {
        allowedRelationshipTypes = mapping.relationshipTypes;
        debugLog(
          `🔍 [buildRelated] Using hardcoded mapping for ${sectionKey}:`,
          {
            allowedRelationshipTypes,
            allowedRelationshipTypesLower: allowedRelationshipTypes.map((t) =>
              String(t).toLowerCase()
            ),
            mapping: mapping,
          }
        );
      }
    }

    // Fallback to global relationshipTypes (or allow all if empty)
    if (allowedRelationshipTypes.length === 0) {
      allowedRelationshipTypes = relationshipTypes;
      debugLog(
        `🔍 [buildRelated] Using global relationshipTypes for ${sectionKey}:`,
        {
          allowedRelationshipTypes,
          allowedRelationshipTypesLower: allowedRelationshipTypes.map((t) =>
            String(t).toLowerCase()
          ),
        }
      );
    }

    // Convert to lowercase for case-insensitive matching
    const allowedTypesLower = allowedRelationshipTypes.map((t) =>
      String(t).toLowerCase().trim()
    );

    debugLog(
      `🔍 [buildRelated] Final relationship types for ${sectionKey}:`,
      {
        original: allowedRelationshipTypes,
        lowercase: allowedTypesLower,
        count: allowedTypesLower.length,
      }
    );

    return buildRelatedEntities(
      editingEntity,
      entityForm,
      removedItems,
      relationships,
      entities,
      allowedTypesLower, // Use section-specific relationship types instead of global list
      relatedEntityType,
      sectionKey,
      combinedAllEntities // Pass merged cache so entities can be looked up by ID
    );
  };

  const getProvidedInterfaces = () => {
    // Merge DB relationships and form for provided interfaces
    const relatedFromForm = entityForm["provided_interfaces"] || [];
    const removedIds = removedItems["provided_interfaces"] || [];

    if (!editingEntity || !relationships) {
      return relatedFromForm.filter(
        (formItem) => !removedIds.includes(formItem.id)
      );
    }

    // RULE: Relationship type "provides" or "exposes" → provided interface
    // Check both directions: Current Entity (source) → provides → Interface (target)
    // OR Interface (source) → provides → Current Entity (target) [interface provides to us]
    let providedLogCount = 0;
    const providesTypes = ["provides", "exposes"];
    const rels = relationships.filter((rel) => {
      const relType = (rel.type || "").toLowerCase().trim();
      if (!providesTypes.includes(relType)) return false;

      const isCurrentEntitySource = rel.source_entity_id === editingEntity.id;
      const isCurrentEntityTarget = rel.target_entity_id === editingEntity.id;

      // Determine which entity is the interface
      let interfaceId, interfaceEntity;
      if (isCurrentEntitySource) {
        // Current Entity → provides → Interface
        interfaceId = rel.target_entity_id;
        interfaceEntity =
          entities.find((e) => e.id === interfaceId) ||
          combinedAllEntities[interfaceId];
      } else if (isCurrentEntityTarget) {
        // Interface → provides → Current Entity (interface provides to us, so we consume it)
        // This should NOT be in provided interfaces
        return false;
      } else {
        return false;
      }

      // Check if target/source is an interface
      const interfaceTypeFromRel = isCurrentEntitySource
        ? rel.target_entity_type
        : rel.source_entity_type;
      const interfaceTypeFromEntity =
        interfaceEntity?.type || interfaceEntity?.entity_type || "";
      const interfaceType = interfaceTypeFromRel || interfaceTypeFromEntity;

      const isInterface =
        interfaceType === "interface" ||
        interfaceType.toLowerCase().includes("interface");

      if (providedLogCount < 5) {
        debugLog(`🔍 [getProvidedInterfaces] Checking relationship:`, {
          relId: rel.id,
          relType: rel.type,
          isCurrentEntitySource: isCurrentEntitySource,
          isCurrentEntityTarget: isCurrentEntityTarget,
          interfaceId: interfaceId,
          interfaceTypeFromRel: interfaceTypeFromRel,
          interfaceTypeFromEntity: interfaceTypeFromEntity,
          interfaceType: interfaceType,
          isInterface: isInterface,
          interfaceEntityFound: !!interfaceEntity,
        });
        providedLogCount++;
      }

      return isInterface;
    });

    debugLog(
      `🔍 [getProvidedInterfaces] Filtered ${rels.length} relationships out of ${relationships.length}`
    );

    const relatedFromDB = rels
      .map((rel) => {
        // For provided interfaces, current entity is always source, interface is always target
        const interfaceId = rel.target_entity_id;
        let iface = entities.find((e) => e.id === interfaceId);

        // Try allEntities if not found in entities array
        if (!iface && combinedAllEntities[interfaceId]) {
          const entityData = combinedAllEntities[interfaceId];
          iface = {
            id: entityData.id || interfaceId,
            name: entityData.name,
            type: entityData.type || entityData.entity_type,
            entity_type: entityData.entity_type || entityData.type,
            description: entityData.description,
          };
        }

        if (!iface) {
          debugLog(
            `🔍 [getProvidedInterfaces] Interface entity not found:`,
            {
              interfaceId: interfaceId,
              relId: rel.id,
              relType: rel.type,
              inEntities: entities.some((e) => e.id === interfaceId),
              inAllEntities: !!combinedAllEntities[interfaceId],
            }
          );
          return null;
        }

        // Also check relationship's target_entity_type
        const interfaceTypeFromRel = rel.target_entity_type;
        const interfaceTypeFromEntity = iface.type || iface.entity_type || "";
        const interfaceType = interfaceTypeFromRel || interfaceTypeFromEntity;

        const isInterface =
          interfaceType === "interface" ||
          interfaceType.toLowerCase().includes("interface");

        if (!isInterface) {
          debugLog(
            `🔍 [getProvidedInterfaces] Entity is not an interface:`,
            {
              interfaceId: interfaceId,
              interfaceName: iface.name,
              interfaceType: interfaceType,
              interfaceTypeFromRel: interfaceTypeFromRel,
              interfaceTypeFromEntity: interfaceTypeFromEntity,
            }
          );
          return null;
        }

        return {
          ...iface,
          activeFrom: rel.active_from,
          activeUntil: rel.active_until,
          description: rel.description,
          status: rel.status,
        };
      })
      .filter(Boolean);

    const filteredDbItems = relatedFromDB.filter(
      (dbItem) => !removedIds.includes(dbItem.id)
    );
    const allRelated = [...filteredDbItems, ...relatedFromForm];
    return allRelated.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );
  };

  const getConsumedInterfaces = () => {
    // Merge DB relationships and form for consumed interfaces
    const relatedFromForm = entityForm["consumed_interfaces"] || [];
    const removedIds = removedItems["consumed_interfaces"] || [];

    if (!editingEntity || !relationships) {
      return relatedFromForm.filter(
        (formItem) => !removedIds.includes(formItem.id)
      );
    }

    // RULE: Relationship type "consumes" → consumed interface
    // OR Interface (source) → provides → Current Entity (target) [interface provides to us, we consume it]
    // Check: Current Entity (source) → consumes → Interface (target)
    // OR Interface (source) → any type → Current Entity (target) [we are target, so we consume]
    let consumedLogCount = 0;
    const consumesTypes = ["consumes", "utilizes"];
    const rels = relationships.filter((rel) => {
      const relType = (rel.type || "").toLowerCase().trim();
      const isCurrentEntitySource = rel.source_entity_id === editingEntity.id;
      const isCurrentEntityTarget = rel.target_entity_id === editingEntity.id;

      // Determine which entity is the interface
      let interfaceId, interfaceEntity;
      if (isCurrentEntitySource && consumesTypes.includes(relType)) {
        // Current Entity → consumes → Interface
        interfaceId = rel.target_entity_id;
        interfaceEntity =
          entities.find((e) => e.id === interfaceId) ||
          combinedAllEntities[interfaceId];
      } else if (isCurrentEntityTarget) {
        // Interface → any type → Current Entity (we are target, so we consume the interface)
        interfaceId = rel.source_entity_id;
        interfaceEntity =
          entities.find((e) => e.id === interfaceId) ||
          combinedAllEntities[interfaceId];
      } else {
        return false;
      }

      // Check if source/target is an interface
      const interfaceTypeFromRel = isCurrentEntitySource
        ? rel.target_entity_type
        : rel.source_entity_type;
      const interfaceTypeFromEntity =
        interfaceEntity?.type || interfaceEntity?.entity_type || "";
      const interfaceType = interfaceTypeFromRel || interfaceTypeFromEntity;

      const isInterface =
        interfaceType === "interface" ||
        interfaceType.toLowerCase().includes("interface");

      if (consumedLogCount < 5) {
        debugLog(`🔍 [getConsumedInterfaces] Checking relationship:`, {
          relId: rel.id,
          relType: rel.type,
          isCurrentEntitySource: isCurrentEntitySource,
          isCurrentEntityTarget: isCurrentEntityTarget,
          interfaceId: interfaceId,
          interfaceTypeFromRel: interfaceTypeFromRel,
          interfaceTypeFromEntity: interfaceTypeFromEntity,
          interfaceType: interfaceType,
          isInterface: isInterface,
          interfaceEntityFound: !!interfaceEntity,
        });
        consumedLogCount++;
      }

      return isInterface;
    });

    debugLog(
      `🔍 [getConsumedInterfaces] Filtered ${rels.length} relationships out of ${relationships.length}`
    );

    const relatedFromDB = rels
      .map((rel) => {
        // Determine which entity is the interface (same logic as filter)
        const isCurrentEntitySource = rel.source_entity_id === editingEntity.id;
        const isCurrentEntityTarget = rel.target_entity_id === editingEntity.id;
        const relType = (rel.type || "").toLowerCase().trim();
        const consumesTypes = ["consumes", "utilizes"];

        let interfaceId;
        if (isCurrentEntitySource && consumesTypes.includes(relType)) {
          // Current Entity → consumes → Interface
          interfaceId = rel.target_entity_id;
        } else if (isCurrentEntityTarget) {
          // Interface → any type → Current Entity
          interfaceId = rel.source_entity_id;
        } else {
          return null;
        }

        let iface = entities.find((e) => e.id === interfaceId);

        // Try allEntities if not found in entities array
        if (!iface && combinedAllEntities[interfaceId]) {
          const entityData = combinedAllEntities[interfaceId];
          iface = {
            id: entityData.id || interfaceId,
            name: entityData.name,
            type: entityData.type || entityData.entity_type,
            entity_type: entityData.entity_type || entityData.type,
            description: entityData.description,
          };
        }

        if (!iface) {
          debugLog(
            `🔍 [getConsumedInterfaces] Interface entity not found:`,
            {
              interfaceId: interfaceId,
              relId: rel.id,
              relType: rel.type,
              inEntities: entities.some((e) => e.id === interfaceId),
              inAllEntities: !!combinedAllEntities[interfaceId],
            }
          );
          return null;
        }

        // Also check relationship's entity type
        const interfaceTypeFromRel = isCurrentEntitySource
          ? rel.target_entity_type
          : rel.source_entity_type;
        const interfaceTypeFromEntity = iface.type || iface.entity_type || "";
        const interfaceType = interfaceTypeFromRel || interfaceTypeFromEntity;

        const isInterface =
          interfaceType === "interface" ||
          interfaceType.toLowerCase().includes("interface");

        if (!isInterface) {
          debugLog(
            `🔍 [getConsumedInterfaces] Entity is not an interface:`,
            {
              interfaceId: interfaceId,
              interfaceName: iface.name,
              interfaceType: interfaceType,
              interfaceTypeFromRel: interfaceTypeFromRel,
              interfaceTypeFromEntity: interfaceTypeFromEntity,
            }
          );
          return null;
        }

        return {
          ...iface,
          activeFrom: rel.active_from,
          activeUntil: rel.active_until,
          description: rel.description,
          status: rel.status,
        };
      })
      .filter(Boolean);

    const filteredDbItems = relatedFromDB.filter(
      (dbItem) => !removedIds.includes(dbItem.id)
    );
    const allRelated = [...filteredDbItems, ...relatedFromForm];
    return allRelated.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );
  };

  // Interface-specific getter functions
  const getInterfaceProvidedByApp = () => {
    // For interfaces, get the single application that provides this interface
    // These are INCOMING relationships where the interface is the TARGET
    if (!editingEntity || !relationships || !entities) return [];

    const providingApps = findRelationshipsByType(
      relationships,
      editingEntity.id,
      ["provides", "exposes"],
      true // isTarget = true (interface is the target)
    );

    const extractedEntities = extractEntitiesFromRelationships(
      providingApps,
      entities,
      "application",
      editingEntity,
      true // isTarget = true
    );

    return mergeRelationshipsWithForm(
      extractedEntities,
      entityForm,
      removedItems,
      "interface_provided_by_app"
    );
  };

  const getInterfaceConsumers = () => {
    // For interfaces, get applications that consume this interface
    // These are relationships where the interface is the target OR source
    // Correct: application -> interface (type: consumes) where interface is TARGET
    // Legacy: interface -> application (type: consumes) where interface is SOURCE
    if (!editingEntity || !relationships || !entities) return [];

    debugLog(
      "🔍 DEBUG getInterfaceConsumers - editingEntity:",
      editingEntity
    );
    debugLog(
      "🔍 DEBUG getInterfaceConsumers - total relationships:",
      relationships.length
    );
    debugLog(
      "🔍 DEBUG getInterfaceConsumers - relationships where interface is target:",
      relationships.filter((rel) => rel.target_entity_id === editingEntity.id)
    );
    debugLog(
      "🔍 DEBUG getInterfaceConsumers - relationships where interface is source:",
      relationships.filter((rel) => rel.source_entity_id === editingEntity.id)
    );

    const consumesTypes = ["consumes", "utilizes"];

    // Find relationships where type is 'consumes' and interface is either target (correct) or source (legacy)
    const consumingApps = relationships
      .filter((rel) => {
        const typeLower = (rel.type || "").toLowerCase();
        const hasCorrectType = consumesTypes.includes(typeLower);
        const isTarget = rel.target_entity_id === editingEntity.id; // Correct: interface is target
        const isSource = rel.source_entity_id === editingEntity.id; // Legacy: interface is source
        const result = hasCorrectType && (isTarget || isSource);
        if (hasCorrectType && (isTarget || isSource)) {
          debugLog(
            "🔍 DEBUG relationship where interface matches - type:",
            typeLower,
            "isTarget:",
            isTarget,
            "isSource:",
            isSource,
            "match:",
            result,
            "full rel:",
            rel
          );
        }
        return result;
      })
      .map((rel) => {
        // Get the consuming application
        // Correct: interface is target -> application is source (source_entity_id)
        // Legacy: interface is source -> application is target (target_entity_id)
        const isCorrectDirection = rel.target_entity_id === editingEntity.id;
        const appId = isCorrectDirection
          ? rel.source_entity_id
          : rel.target_entity_id;
        const appEntity = entities.find((e) => {
          if (e.id !== appId) return false;
          // Check both type and entity_type fields, case-insensitively
          const eType = (e.type || "").toLowerCase();
          const eEntityType = (e.entity_type || "").toLowerCase();
          return eType === "application" || eEntityType === "application";
        });
        debugLog(
          "🔍 DEBUG found app entity:",
          appEntity
            ? { id: appEntity.id, name: appEntity.name, type: appEntity.type }
            : "NOT FOUND"
        );
        if (!appEntity) return null;
        return {
          ...appEntity,
          activeFrom: rel.active_from,
          activeUntil: rel.active_until,
          description: rel.description,
          status: rel.status,
        };
      })
      .filter(Boolean);

    // Merge with form data
    const relatedFromForm = entityForm.interface_consumed_by_app || [];
    const removedIds = removedItems.interface_consumed_by_app || [];
    const filteredDbItems = consumingApps.filter(
      (dbItem) => !removedIds.includes(dbItem.id)
    );
    const allRelated = [...filteredDbItems, ...relatedFromForm];
    const uniqueRelated = allRelated.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );

    debugLog("🔍 getInterfaceConsumers - consumingApps:", consumingApps);
    debugLog("🔍 getInterfaceConsumers - uniqueRelated:", uniqueRelated);

    return uniqueRelated;
  };

  const getInterfaceImplementedByITComponents = () => {
    // For interfaces, get IT components that implement this interface
    // These are OUTGOING relationships where the interface is the SOURCE
    if (!editingEntity || !relationships || !entities) return [];

    const implementingComponents = findRelationshipsByType(
      relationships,
      editingEntity.id,
      ["implements", "uses"],
      false // isTarget = false (interface is the source)
    );

    const extractedEntities = extractEntitiesFromRelationships(
      implementingComponents,
      entities,
      "technology-component",
      editingEntity,
      false // isTarget = false
    );

    return mergeRelationshipsWithForm(
      extractedEntities,
      entityForm,
      removedItems,
      "technology_component_used_by_interface"
    );
  };

  const getInterfaceTransferredData = () => {
    // For interfaces, get data objects that are transferred by this interface
    const allDataObjects = buildRelated(
      "data-object",
      "interface_transfers_data"
    );

    // Get data objects from relationships (database)
    const fromDb = allDataObjects.filter((dataEntity) => {
      // Check if this interface transfers the data object using dynamic relationship types
      const transfersTypes = [
        "accesses",
        "transfers",
        "reads",
        "writes",
        "creates",
        "updates",
        "deletes",
      ];
      const isTransferred = relationships.some(
        (rel) =>
          rel.source_entity_id === editingEntity?.id &&
          rel.target_entity_id === dataEntity.id &&
          transfersTypes.includes(rel.type?.toLowerCase())
      );

      return isTransferred;
    });

    // Get data objects from form state (newly added items)
    const formItems = entityForm.interface_transfers_data || [];
    const removedIds = removedItems.interface_transfers_data || [];

    // Filter out removed items from database results
    const filteredDb = fromDb.filter((item) => !removedIds.includes(item.id));

    // Merge database items with form items
    const merged = [...filteredDb, ...formItems];

    // Deduplicate by id
    const unique = [];
    const seen = new Set();
    for (const item of merged) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        unique.push(item);
      }
    }

    debugLog(`🔍 getInterfaceTransferredData:`, {
      fromDbCount: fromDb.length,
      formItemsCount: formItems.length,
      removedCount: removedIds.length,
      mergedCount: merged.length,
      uniqueCount: unique.length,
    });

    return unique;
  };

  const getInterfaceAffectedByInitiative = () => {
    // For interfaces, get initiatives that affect this interface
    // These are INCOMING relationships where the interface is the TARGET
    if (!editingEntity || !relationships || !entities) return [];

    const affectingInitiatives = findRelationshipsByType(
      relationships,
      editingEntity.id,
      ["affects", "impacts"],
      true // isTarget = true (interface is the target)
    );

    // Extract entities using the helper function (which handles type matching correctly)
    const extractedEntities = extractEntitiesFromRelationships(
      affectingInitiatives,
      entities,
      "initiative",
      editingEntity,
      true // isTarget = true
    );

    debugLog(
      "🔍 Extracted initiatives for interface:",
      extractedEntities.length,
      extractedEntities
    );

    return mergeRelationshipsWithForm(
      extractedEntities,
      entityForm,
      removedItems,
      "interface_affected_by_initiative"
    );
  };

  // Derived: Providers supporting an Initiative via Apps/IT Components the Initiative affects/realizes
  const getProvidersSupportingInitiative = () => {
    if (!editingEntity || !relationships || !entities) return [];
    // Only for initiatives
    const currentType = (
      editingEntity.type ||
      editingEntity.entity_type ||
      ""
    ).toLowerCase();
    if (currentType !== "initiative") return [];

    const affectsTypes = ["affects", "realizes", "builds", "realizes/builds"];
    // Normalize and include both provider->asset and asset->provider styles
    const providerOutTypes = [
      "provides",
      "provides services to",
      "serves",
      "provides to",
    ];
    const providerInTypes = [
      "consumes service from",
      "consumes from",
      "consumes",
      "uses service from",
    ];

    // Collect target application and itcomponent ids that this initiative affects/realizes
    const affectedAppIds = new Set(
      relationships
        .filter(
          (rel) =>
            rel.source_entity_id === editingEntity.id &&
            affectsTypes.includes((rel.type || "").toLowerCase())
        )
        .map((rel) => {
          const target = entities.find((e) => e.id === rel.target_entity_id);
          const tType = (
            target?.type ||
            target?.entity_type ||
            ""
          ).toLowerCase();
          return tType === "application" ? rel.target_entity_id : null;
        })
        .filter(Boolean)
    );
    const affectedItIds = new Set(
      relationships
        .filter(
          (rel) =>
            rel.source_entity_id === editingEntity.id &&
            affectsTypes.includes((rel.type || "").toLowerCase())
        )
        .map((rel) => {
          const target = entities.find((e) => e.id === rel.target_entity_id);
          const tType = (
            target?.type ||
            target?.entity_type ||
            ""
          ).toLowerCase();
          return tType === "technology-component" || tType === "itcomponent"
            ? rel.target_entity_id
            : null;
        })
        .filter(Boolean)
    );

    const providerIds = new Set();

    // Case 1: provider -> (app|it) with providerOutTypes
    relationships.forEach((rel) => {
      const typeLower = (rel.type || "").toLowerCase();
      if (!providerOutTypes.includes(typeLower)) return;
      if (
        !(
          affectedAppIds.has(rel.target_entity_id) ||
          affectedItIds.has(rel.target_entity_id)
        )
      )
        return;
      const src = entities.find((e) => e.id === rel.source_entity_id);
      const sType = (src?.type || src?.entity_type || "").toLowerCase();
      if (sType === "provider") providerIds.add(rel.source_entity_id);
    });

    // Case 2: (app|it) -> provider with providerInTypes
    relationships.forEach((rel) => {
      const typeLower = (rel.type || "").toLowerCase();
      if (!providerInTypes.includes(typeLower)) return;
      if (
        !(
          affectedAppIds.has(rel.source_entity_id) ||
          affectedItIds.has(rel.source_entity_id)
        )
      )
        return;
      const tgt = entities.find((e) => e.id === rel.target_entity_id);
      const tType = (tgt?.type || tgt?.entity_type || "").toLowerCase();
      if (tType === "provider") providerIds.add(rel.target_entity_id);
    });

    const relatedFromDB = Array.from(providerIds)
      .map((pid) => entities.find((e) => e.id === pid))
      .filter(Boolean)
      .map((p) => ({ ...p }));

    // Merge with form data (in case user already added providers directly)
    const relatedFromForm =
      entityForm["provider_supports_initiative_reverse"] || [];
    const removedIds =
      removedItems["provider_supports_initiative_reverse"] || [];
    const filteredDb = relatedFromDB.filter(
      (item) => !removedIds.includes(item.id)
    );
    const allRelated = [...filteredDb, ...relatedFromForm];
    return allRelated.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );
  };

  const getDataObjectTransferredByInterface = () => {
    // For data objects, get interfaces that transfer this data object
    // These are INCOMING relationships where the data object is the TARGET
    if (!editingEntity || !relationships || !entities) return [];

    debugLog(
      "🔍 DEBUG getDataObjectTransferredByInterface - editingEntity:",
      editingEntity
    );

    const transferringInterfaces = findRelationshipsByType(
      relationships,
      editingEntity.id,
      [
        "transfers",
        "accesses",
        "reads",
        "writes",
        "creates",
        "updates",
        "deletes",
      ],
      true // isTarget = true (data object is the target)
    );

    const extractedEntities = extractEntitiesFromRelationships(
      transferringInterfaces,
      entities,
      "interface",
      editingEntity,
      true // isTarget = true
    );

    return mergeRelationshipsWithForm(
      extractedEntities,
      entityForm,
      removedItems,
      "data_object_transferred_by_interface"
    );
  };

  const getDataObjectAffectedByInitiative = () => {
    // For data objects, get initiatives that affect this data object
    // These are INCOMING relationships where the data object is the TARGET
    if (!editingEntity || !relationships || !entities) return [];

    debugLog(
      "🔍 DEBUG getDataObjectAffectedByInitiative - editingEntity:",
      editingEntity
    );

    const affectingInitiatives = findRelationshipsByType(
      relationships,
      editingEntity.id,
      ["affects"],
      true // isTarget = true (data object is the target)
    );

    const extractedEntities = extractEntitiesFromRelationships(
      affectingInitiatives,
      entities,
      "initiative",
      editingEntity,
      true // isTarget = true
    );

    return mergeRelationshipsWithForm(
      extractedEntities,
      entityForm,
      removedItems,
      "data_object_affected_by_initiative"
    );
  };

  const getInitiativesAffectingTechnologyComponent = () => {
    // For technology components, get initiatives that affect this IT component
    // These are INCOMING relationships where the IT component is the TARGET
    if (!editingEntity || !relationships || !entities) return [];

    debugLog(
      "🔍 DEBUG getInitiativesAffectingTechnologyComponent - editingEntity:",
      editingEntity
    );

    const affectingInitiatives = findRelationshipsByType(
      relationships,
      editingEntity.id,
      ["affects", "impacts", "realizes", "builds"],
      true // isTarget = true (IT component is the target)
    );

    debugLog(
      "🔍 Found affecting initiatives relationships:",
      affectingInitiatives
    );

    // Extract entities, checking for multiple type variations (initiative, Initiative, etc.)
    const extractedEntities = affectingInitiatives
      .map((rel) => {
        const sourceEntityId = rel.source_entity_id;
        const entity = entities.find(
          (e) =>
            e.id === sourceEntityId &&
            (e.type === "initiative" ||
              e.type === "Initiative" ||
              e.entity_type === "initiative")
        );

        if (!entity) {
          debugLog("🔍 Entity not found for relationship:", {
            source_id: sourceEntityId,
            relationship_type: rel.type,
            available_entities: entities
              .map((e) => ({ id: e.id, type: e.type, name: e.name }))
              .slice(0, 5),
          });
          return null;
        }

        return {
          ...entity,
          activeFrom: rel.active_from,
          activeUntil: rel.active_until,
          description: rel.description,
          status: rel.status,
        };
      })
      .filter(Boolean);

    debugLog(
      "🔍 Extracted initiatives:",
      extractedEntities.length,
      extractedEntities
    );

    return mergeRelationshipsWithForm(
      extractedEntities,
      entityForm,
      removedItems,
      "initiative_affects_it_reverse"
    );
  };

  const getInterfacesImplementedByTechnologyComponent = () => {
    // For technology components, get interfaces that are implemented using this IT component
    // These are INCOMING relationships where the IT component is the TARGET
    if (!editingEntity || !relationships || !entities) return [];

    const implementingInterfaces = findRelationshipsByType(
      relationships,
      editingEntity.id,
      ["implements", "uses"],
      true // isTarget = true (IT component is the target)
    );

    const extractedEntities = implementingInterfaces
      .map((rel) => {
        const sourceEntityId = rel.source_entity_id;
        const entity = entities.find(
          (e) =>
            e.id === sourceEntityId &&
            (e.type === "interface" ||
              e.type === "Interface" ||
              e.entity_type === "interface")
        );

        // Also check combinedAllEntities cache
        if (!entity && combinedAllEntities[sourceEntityId]) {
          const entityData = combinedAllEntities[sourceEntityId];
          const entityType = entityData.type || entityData.entity_type || "";
          if (
            entityType === "interface" ||
            entityType.toLowerCase() === "interface"
          ) {
            return {
              id: entityData.id || sourceEntityId,
              name: entityData.name,
              type: entityData.type || entityData.entity_type,
              entity_type: entityData.entity_type || entityData.type,
              description: entityData.description || rel.description,
              activeFrom: rel.active_from,
              activeUntil: rel.active_until,
              status: rel.status,
            };
          }
        }

        // Also check relationship's entity type fields
        if (!entity && rel.source_entity_type) {
          const relEntityType = rel.source_entity_type.toLowerCase();
          if (relEntityType === "interface") {
            return {
              id: sourceEntityId,
              name: combinedAllEntities[sourceEntityId]?.name || `Interface ${sourceEntityId.substring(0, 8)}`,
              type: rel.source_entity_type,
              entity_type: rel.source_entity_type,
              description: combinedAllEntities[sourceEntityId]?.description || rel.description || "",
              activeFrom: rel.active_from,
              activeUntil: rel.active_until,
              status: rel.status,
            };
          }
        }

        if (!entity) {
          return null;
        }

        return {
          ...entity,
          activeFrom: rel.active_from,
          activeUntil: rel.active_until,
          description: rel.description,
          status: rel.status,
        };
      })
      .filter(Boolean);

    return mergeRelationshipsWithForm(
      extractedEntities,
      entityForm,
      removedItems,
      "technology_component_used_by_interface"
    );
  };

  const getProvidersOfferingTechnologyComponent = () => {
    // For technology components, get providers that offer/provide this IT component
    // Registry says direction: "either", so handle both incoming and outgoing
    if (!editingEntity || !relationships || !entities) return [];

    debugLog(
      "🔍 DEBUG getProvidersOfferingTechnologyComponent - editingEntity:",
      editingEntity
    );

    // Find relationships where technology component is the TARGET (incoming)
    const incomingProviders = findRelationshipsByType(
      relationships,
      editingEntity.id,
      ["provides", "offers", "supplies"],
      true // isTarget = true (IT component is the target)
    );

    // Also find relationships where technology component is the SOURCE (outgoing)
    // This handles cases where the relationship direction is reversed in the database
    const outgoingProviders = findRelationshipsByType(
      relationships,
      editingEntity.id,
      ["provides", "offers", "supplies"],
      false // isTarget = false (IT component is the source)
    );

    // Combine both directions
    const providingProviders = [...incomingProviders, ...outgoingProviders];

    debugLog(
      "🔍 Found providing providers relationships:",
      {
        incoming: incomingProviders.length,
        outgoing: outgoingProviders.length,
        total: providingProviders.length,
        relationships: providingProviders
      }
    );

    // Extract entities, checking for multiple type variations
    const extractedEntities = providingProviders
      .map((rel) => {
        // For incoming: provider is source, for outgoing: provider is target
        const isIncoming = rel.target_entity_id === editingEntity.id;
        const providerEntityId = isIncoming ? rel.source_entity_id : rel.target_entity_id;
        
        const entity = entities.find(
          (e) =>
            e.id === providerEntityId &&
            (e.type === "provider" ||
              e.type === "Provider" ||
              e.entity_type === "provider")
        );

        // Also check combinedAllEntities cache
        if (!entity && combinedAllEntities[providerEntityId]) {
          const entityData = combinedAllEntities[providerEntityId];
          const entityType = entityData.type || entityData.entity_type || "";
          if (
            entityType === "provider" ||
            entityType.toLowerCase() === "provider"
          ) {
            return {
              id: entityData.id || providerEntityId,
              name: entityData.name,
              type: entityData.type || entityData.entity_type,
              entity_type: entityData.entity_type || entityData.type,
              description: entityData.description || rel.description,
              activeFrom: rel.active_from,
              activeUntil: rel.active_until,
              status: rel.status,
            };
          }
        }

        // Also check relationship's entity type fields
        if (!entity) {
          const relEntityType = isIncoming ? rel.source_entity_type : rel.target_entity_type;
          if (relEntityType && (
            relEntityType.toLowerCase() === "provider"
          )) {
            return {
              id: providerEntityId,
              name: combinedAllEntities[providerEntityId]?.name || `Provider ${providerEntityId.substring(0, 8)}`,
              type: relEntityType,
              entity_type: relEntityType,
              description: combinedAllEntities[providerEntityId]?.description || rel.description || "",
              activeFrom: rel.active_from,
              activeUntil: rel.active_until,
              status: rel.status,
            };
          }
        }

        if (!entity) {
          debugLog("🔍 Entity not found for relationship:", {
            provider_id: providerEntityId,
            relationship_type: rel.type,
            is_incoming: isIncoming,
            source_id: rel.source_entity_id,
            target_id: rel.target_entity_id,
            editing_entity_id: editingEntity.id,
            available_entities: entities
              .map((e) => ({ id: e.id, type: e.type, name: e.name }))
              .slice(0, 5),
          });
          return null;
        }

        return {
          ...entity,
          activeFrom: rel.active_from,
          activeUntil: rel.active_until,
          description: rel.description,
          status: rel.status,
        };
      })
      .filter(Boolean);

    debugLog(
      "🔍 Extracted providers:",
      extractedEntities.length,
      extractedEntities
    );

    return mergeRelationshipsWithForm(
      extractedEntities,
      entityForm,
      removedItems,
      "it_provided_by_provider_reverse"
    );
  };

  const handleAddToSection = (sectionName, itemOrItems) => {
    const incomingItems = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
    const itemsToAdd = incomingItems.filter((item) => item?.id);
    if (!itemsToAdd.length) return;

    debugLog("🔍 handleAddToSection called:", {
      sectionName,
      items: JSON.stringify(itemsToAdd, null, 2),
    });

    // Try rules-populated data, otherwise use SectionConfig definition
    let sectionData = populatedSections[sectionName];
    const sectionDefs = SECTION_CATALOG[effectiveEntityType] || [];
    const sectionDef = sectionDefs.find((d) => d.key === sectionName);
    if (!sectionData && sectionDef) {
      sectionData = {
        title: sectionDef.label,
        relatedType:
          (sectionDef.relatedEntityTypes && sectionDef.relatedEntityTypes[0]) ||
          "item",
        direction: sectionDef.direction,
      };
    }
    debugLog("🔍 sectionData:", JSON.stringify(sectionData, null, 2));
    if (!sectionData && !sectionDef) {
      debugLog("❌ No section metadata found for:", sectionName);
      setEntityForm((prev) => ({
        ...prev,
        [sectionName]: [...(prev[sectionName] || []), ...itemsToAdd],
      }));
      setRemovedItems((prev) => {
        const currentRemoved = prev[sectionName] || [];
        const idsToRestore = itemsToAdd
          .map((item) => item.id)
          .filter((id) => currentRemoved.includes(id));
        if (!idsToRestore.length) return prev;
        const filtered = currentRemoved.filter((id) => !idsToRestore.includes(id));
        return {
          ...prev,
          [sectionName]: filtered,
        };
      });
      return;
    }

    // Same batched map as section rows (avoids duplicate resolveSectionItems work).
    let currentDisplayedItems = [];
    if (sectionDef) {
      currentDisplayedItems = resolvedSectionItemsMap[sectionName] || [];
    }

    const duplicate = itemsToAdd.find((item) =>
      currentDisplayedItems.some((existingItem) => existingItem.id === item.id)
    );

    if (duplicate) {
      setSectionErrors((prev) => ({
        ...prev,
        [sectionName]: `"${duplicate.name}" is already added to this relationship section. Duplicate entities are not allowed.`,
      }));
      setTimeout(() => {
        setSectionErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[sectionName];
          return newErrors;
        });
      }, 5000);
      return;
    }

    const uniqueItems = itemsToAdd.filter(
      (item, index, arr) => arr.findIndex((x) => x.id === item.id) === index
    );

    const validationRule = getValidationRule(sectionName);
    if (validationRule && validationRule.maxItems) {
      if (
        currentDisplayedItems.length + uniqueItems.length >
        validationRule.maxItems
      ) {
        setSectionErrors((prev) => ({
          ...prev,
          [sectionName]:
            validationRule.message ||
            `Only ${validationRule.maxItems} item${validationRule.maxItems > 1 ? "s" : ""} allowed. Please remove existing items first.`,
        }));
        setTimeout(() => {
          setSectionErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[sectionName];
            return newErrors;
          });
        }, 5000);
        return;
      }
    }

    setSectionErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[sectionName];
      return newErrors;
    });

    debugLog("🔍 Adding items to form state:", {
      sectionName,
      items: JSON.stringify(uniqueItems, null, 2),
    });
    setEntityForm((prev) => {
      const newForm = {
        ...prev,
        [sectionName]: [...(prev[sectionName] || []), ...uniqueItems],
      };
      debugLog("🔍 New form state:", JSON.stringify(newForm, null, 2));
      return newForm;
    });
    setRemovedItems((prev) => {
      const currentRemoved = prev[sectionName] || [];
      const idsToRestore = uniqueItems
        .map((item) => item.id)
        .filter((id) => currentRemoved.includes(id));
      if (!idsToRestore.length) return prev;
      const filtered = currentRemoved.filter((id) => !idsToRestore.includes(id));
      return {
        ...prev,
        [sectionName]: filtered,
      };
    });
  };

  const handleRemoveFromSection = (sectionName, itemId) => {
    debugLog("🔍 Removing item:", itemId, "from section:", sectionName);
    debugLog("🔍 Current form state:", entityForm[sectionName]);
    debugLog("🔍 Current removed items:", removedItems[sectionName]);

    setEntityForm((prev) => {
      const currentItems = prev[sectionName] || [];
      const filtered = currentItems.filter((item) => item.id !== itemId);
      debugLog("🔍 Filtered items:", filtered);
      return {
        ...prev,
        [sectionName]: filtered,
      };
    });

    setRemovedItems((prev) => {
      const currentRemoved = prev[sectionName] || [];
      const updatedRemoved = [...currentRemoved, itemId];
      debugLog("🔍 Updated removed items:", updatedRemoved);
      return {
        ...prev,
        [sectionName]: updatedRemoved,
      };
    });
  };

  const attributeOverrideKey = (sectionKey, item) => {
    if (item?.relationshipId) return `rel:${item.relationshipId}`;
    if (item?.id) return `pending:${sectionKey}:${item.id}`;
    return null;
  };

  const applyAttributeOverrides = useCallback(
    (sectionKey, items) => {
      if (!Array.isArray(items) || !items.length) return items || [];
      return items.map((item) => {
        const key = attributeOverrideKey(sectionKey, item);
        const ov = key ? relationshipAttributeOverrides[key] : null;
        return ov ? { ...item, ...ov } : item;
      });
    },
    [relationshipAttributeOverrides]
  );

  const handleUpdateAttributes = async (sectionKey, item, { api: apiPayload, local }) => {
    if (!item) return;
    if (item.relationshipId) {
      await api.updateRelationship(item.relationshipId, apiPayload);
      const key = attributeOverrideKey(sectionKey, item);
      if (key) {
        setRelationshipAttributeOverrides((prev) => ({
          ...prev,
          [key]: { ...local },
        }));
      }
      return;
    }
    // Staged (not yet persisted) add — update form row only.
    setEntityForm((prev) => ({
      ...prev,
      [sectionKey]: (prev[sectionKey] || []).map((row) =>
        row?.id === item.id ? { ...row, ...local } : row
      ),
    }));
  };

  const handleRemoveFromRule = (rule, itemId) => {
    const key = getSectionKeyForRule(rule);
    if (!key) return;
    handleRemoveFromSection(key, itemId);
  };

  // Debug logging
  debugLog(
    "🔍 populatedSections:",
    JSON.stringify(populatedSections, null, 2)
  );
  debugLog("🔍 populatedSections keys:", Object.keys(populatedSections));
  if (populatedSections.interface_provided_by_app) {
    debugLog(
      "🔍 INTERFACE PROVIDED BY APP SECTION FOUND:",
      populatedSections.interface_provided_by_app
    );
  } else {
    debugLog("🔍 INTERFACE PROVIDED BY APP SECTION NOT FOUND");
  }

  const relationshipsLoadingSpinner = (
    <svg
      className="animate-spin w-4 h-4 shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  // Show loading state while relationship rules are being fetched
  if (rulesLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
            🔗 Relationships
          </h2>
        </div>
        <div className="flex items-center justify-center gap-2 py-8">
          {relationshipsLoadingSpinner}
          <div className="text-gray-400 text-sm">Loading relationships…</div>
        </div>
      </div>
    );
  }

  // Validation: log mapping coverage for this entity type (dev console)
  if (!compactMode) {
    try {
      const report = validateMappingsForEntity({
        entityType: entityForm.type,
        rulesForType,
        populatedSections,
        relationships,
        entities,
        editingEntity,
      });
      debugLog("🧪 Relationship mapping coverage report:", report);
    } catch (e) {
      debugLog("Mapping validator error:", e);
    }
  }

  // Show error state if relationship rules failed to load
  if (rulesError) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
            🔗 Relationships
          </h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-red-400 text-sm">
            Error loading relationships: {rulesError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Relationships Section Header */}
      {!compactMode && (
        <div>
          <h2 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
            🔗 Relationships
          </h2>
        </div>
      )}

      {relationshipsPopulateLoading && (
        <div className="bg-blue-900/30 border border-blue-700 text-blue-200 text-sm px-4 py-3 rounded flex items-center gap-2">
          {relationshipsLoadingSpinner}
          <span>Loading connections for this item…</span>
        </div>
      )}

      {referenceStatus === "loading" && (
        <div className="bg-blue-900/30 border border-blue-700 text-blue-200 text-sm px-4 py-2 rounded">
          Loading reference entities for this form…
        </div>
      )}

      {referenceStatus === "error" && referenceError && (
        <div className="bg-red-900/30 border border-red-700 text-red-200 text-sm px-4 py-2 rounded">
          Unable to load reference entities automatically. Some Add dialogs may
          be incomplete. {referenceError}
        </div>
      )}


      {/* Debug: All relationships touching this entity */}
      {!compactMode && editingEntity && relationships && entities && (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-300 mb-2">
            All relationships for this entity
          </div>
          <div className="max-h-48 overflow-auto">
            {relationships
              .filter(
                (r) =>
                  r.source_entity_id === editingEntity.id ||
                  r.target_entity_id === editingEntity.id
              )
              .map((r) => {
                const isOutgoing = r.source_entity_id === editingEntity.id;
                const otherId = isOutgoing
                  ? r.target_entity_id
                  : r.source_entity_id;

                // Get relationship's entity type fields - NOW ALWAYS AVAILABLE FROM BACKEND
                // Backend API now includes source_entity_type and target_entity_type in all relationship responses
                const relEntityType = isOutgoing
                  ? r.target_entity_type
                  : r.source_entity_type;

                // Use the EXACT same lookup strategy as resolveSectionItems (which works in Dependencies):
                // 1. Check entities array (includes all merged sources) - this is the normalized list
                let other = entities.find((e) => e.id === otherId);

                // 2. Check allEntities prop directly (CRITICAL - this is what resolveSectionItems uses)
                if (!other && allEntities[otherId]) {
                  const entityData = allEntities[otherId];
                  other = {
                    id: entityData.id || otherId,
                    name: entityData.name,
                    type: normalizeType(
                      entityData.type || entityData.entity_type
                    ),
                    entity_type: normalizeType(
                      entityData.entity_type || entityData.type
                    ),
                    description: entityData.description,
                  };
                }

                // 3. Try combinedAllEntities (includes allEntities and localEntityCache)
                if (!other) {
                  const cached = combinedAllEntities[otherId];
                  if (cached && typeof cached === "object") {
                    other = {
                      id: cached.id || otherId,
                      name: cached.name,
                      type: normalizeType(cached.type || cached.entity_type),
                      entity_type: normalizeType(
                        cached.entity_type || cached.type
                      ),
                      description: cached.description,
                    };
                  }
                }

                // If still not found, try localEntityCache directly (most recent)
                if (!other && localEntityCache[otherId]) {
                  const cached = localEntityCache[otherId];
                  other = {
                    id: cached.id || otherId,
                    name: cached.name,
                    type: normalizeType(cached.type || cached.entity_type),
                    entity_type: normalizeType(
                      cached.entity_type || cached.type
                    ),
                    description: cached.description,
                  };
                }

                // If still not found but we have type from relationship, create minimal entity
                if (!other && relEntityType) {
                  other = {
                    id: otherId,
                    name: `Entity ${otherId.substring(0, 8)}...`, // Placeholder name
                    type: normalizeType(relEntityType),
                    entity_type: normalizeType(relEntityType),
                  };
                }

                // If entity exists but has no type, use relationship's type as fallback
                if (
                  other &&
                  !other.type &&
                  !other.entity_type &&
                  relEntityType
                ) {
                  other = {
                    ...other,
                    type: normalizeType(relEntityType),
                    entity_type: normalizeType(relEntityType),
                  };
                }

                // If still missing and not already fetching, trigger fetch
                if (!other && !pendingFetches.current.has(otherId)) {
                  pendingFetches.current.add(otherId);
                  entityAPI
                    .getEntityById(otherId)
                    .then((fetched) => {
                      if (fetched) {
                        // Ensure type information is preserved and normalized
                        const entityWithType = {
                          ...fetched,
                          type: normalizeType(
                            fetched.type || fetched.entity_type
                          ),
                          entity_type: normalizeType(
                            fetched.entity_type || fetched.type
                          ),
                        };
                        setLocalEntityCache((prev) => ({
                          ...prev,
                          [otherId]: entityWithType,
                        }));
                      }
                      pendingFetches.current.delete(otherId);
                    })
                    .catch(() => {
                      pendingFetches.current.delete(otherId);
                    });
                }

                const isMissing = !other;
                // Get final type - prioritize entity's type, then relationship's type, then unknown
                // Normalize the type for display
                let finalType = other?.type || other?.entity_type;
                if (finalType) {
                  // Normalize entity type for display (already normalized, but ensure consistency)
                  finalType = normalizeEntityType(finalType);
                } else if (relEntityType) {
                  // Fallback to relationship's entity type if available
                  finalType = normalizeEntityType(relEntityType);
                } else {
                  finalType = "(unknown)";
                }
                return {
                  id: r.id,
                  type: r.type,
                  direction: isOutgoing ? "outgoing" : "incoming",
                  otherId,
                  otherName: other?.name || "(unknown)",
                  otherType: finalType,
                  isMissing,
                };
              })
              .slice(0, 100)
              .map((row) => {
                // Debug: Log the relationship details
                if (row.isMissing) {
                  debugWarn(`⚠️ Missing entity in relationship:`, {
                    relationshipId: row.id,
                    relationshipType: row.type,
                    direction: row.direction,
                    missingEntityId: row.otherId,
                    currentEntityId: editingEntity.id,
                    currentEntityName: editingEntity.name,
                  });
                }
                // Use friendly label for outgoing "consumes" from applications
                let displayType = row.type;
                if (
                  row.direction === "outgoing" &&
                  (row.type?.toLowerCase() === "consumes" ||
                    row.type?.toLowerCase() === "utilizes") &&
                  editingEntity &&
                  (editingEntity.type === "application" ||
                    editingEntity.entity_type === "application")
                ) {
                  displayType = "provides";
                }
                return (
                  <div
                    key={row.id}
                    className={`text-xs py-1 border-b border-gray-800 ${
                      row.isMissing ? "text-yellow-400" : "text-gray-200"
                    }`}
                  >
                    <span className="text-gray-400">[{row.direction}]</span>{" "}
                    {displayType} → {row.otherName}{" "}
                    <span className="text-gray-400">({row.otherType})</span>
                    {row.isMissing && (
                      <span
                        className="text-yellow-500 text-xs ml-2"
                        title={`Entity ID: ${row.otherId}`}
                      >
                        ⚠️ Missing Entity (ID: {row.otherId.substring(0, 8)}...)
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Single Column Layout for Relationship Sections (populated based on entity type) */}
      <div className="space-y-6">
        {configsToRender.map(
          ({
            sectionKey,
            sectionData,
            defOrEntry,
            rawRelatedTypes = [],
            availableItems,
            normalizedRelatedTypes,
          }) => {
            const items = applyAttributeOverrides(
              sectionKey,
              resolvedSectionItemsMap[sectionKey] || []
            );

            debugLog(
              `🔍 Getting items for ${sectionKey}:`,
              items.length,
              items
            );

            // Note: Type fetching is handled by the useEffect that processes sectionConfigsMemo.missingTypes
            // No need for useEffect here as it violates React's rules of hooks (cannot be in loops)

            if (availableItems.length === 0) {
              const fallbackFetched = normalizedRelatedTypes.flatMap(
                (type) => fetchedEntities[type] || []
              );
              if (fallbackFetched.length > 0) {
                availableItems = fallbackFetched;
              }
            }

            const relatedTypeForLabel = normalizeType(
              rawRelatedTypes?.[0] || "item"
            );
            const humanLabel = getHumanLabel(
              sectionKey,
              relatedTypeForLabel,
              entityForm.type,
              items.length
            );

            const icon =
              sectionData.icon ||
              getRelationshipIcon(
                relatedTypeForLabel,
                entityTypesMap,
                sectionData.direction
              );

            const displayTitle =
              sectionData.title || sectionData.label || "🔗 Relationships";

            // Filter out items that are already in the section from availableItems
            const itemsIds = new Set(
              (items || []).map((item) => item?.id).filter(Boolean)
            );
            const filteredAvailableItems = (
              Array.isArray(availableItems) ? availableItems : []
            ).filter((availableItem) => {
              if (!availableItem || !availableItem.id) return false;
              // Don't show items that are already in the section
              if (itemsIds.has(availableItem.id)) return false;
              return true;
            });

            if (sectionKey.includes("platform")) {
              debugLog(`🔍 Platform section "${sectionKey}":`, {
                availableItemsCount: availableItems?.length || 0,
                filteredAvailableItemsCount: filteredAvailableItems.length,
                itemsCount: items?.length || 0,
                itemsIds: Array.from(itemsIds),
                sampleAvailable: filteredAvailableItems
                  .slice(0, 3)
                  .map((e) => ({
                    id: e.id,
                    name: e.name,
                    type: e.type || e.entity_type,
                  })),
              });
            }

            return (
              <div key={sectionKey}>
                {/* Section-specific Error Message */}
                {sectionErrors[sectionKey] && (
                  <div className="bg-red-900 border border-red-700 rounded-lg p-3 mb-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-4 w-4 text-red-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-2">
                        <p className="text-xs text-red-200">
                          {sectionErrors[sectionKey]}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <EntityRelationshipSection
                  title={displayTitle}
                  icon={
                    sectionKey === "related_successors"
                      ? "➡️"
                      : sectionKey.includes("_parents")
                        ? "⬆️"
                        : icon
                  }
                  items={items}
                  availableItems={filteredAvailableItems}
                  typeMetaMap={typeMetaMap}
                  addLabel={humanLabel}
                  onAdd={(item) => handleAddToSection(sectionKey, item)}
                  onRemove={(itemId) => {
                    // Always use handleRemoveFromSection for removal
                    handleRemoveFromSection(sectionKey, itemId);
                  }}
                  onUpdateAttributes={(item, payload) =>
                    handleUpdateAttributes(sectionKey, item, payload)
                  }
                  showAdditionalFields={true}
                  readOnly={readOnly}
                  maxItems={getValidationRule(sectionKey)?.maxItems || null}
                  loadingAvailable={false}
                  loadingItems={relationshipsPopulateLoading}
                  relatedEntityType={normalizedRelatedTypes[0] || null}
                  sourceEntityId={editingEntity?.id || null}
                  useLazyLoading={true}
                />
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export default EntityRelationships;


